import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  ChannelTag,
  CREATE_IDEA_CHANNEL_TAG,
  DELETE_IDEA_CHANNEL_TAG,
  GET_CHANNEL_TAGS,
  Idea,
} from '@frontend/data-access';
import { Button, Dropdown, Popconfirm, MenuProps } from 'antd';
import { ItemType } from 'antd/lib/menu/interface';
import useTranslation from 'next-translate/useTranslation';

function IdeaChannelTagList({
  idea,
  onFinish,
}: {
  idea: Idea;
  onFinish: () => void;
}) {
  const { t } = useTranslation('common');

  const { data: channelTags } = useQuery<{ channelTags: ChannelTag[] }>(
    GET_CHANNEL_TAGS
  );

  const menuItems: ItemType[] =
    channelTags?.channelTags
      .filter((item) => !idea.channelTag?.find((t) => t.id === item.id))
      .map((item) => ({
        key: item.id,
        label: item.name,
        type: 'item',
      })) || [];

  // Add empty state menu item if no tags available
  if (menuItems.length === 0) {
    menuItems.push({
      key: 'no-data',
      label: t('no_data'),
      disabled: true,
    });
  }

  const handleCompleted = () => {
    onFinish();
  };

  const [createIdeaChannelTag] = useMutation(CREATE_IDEA_CHANNEL_TAG, {
    onCompleted: handleCompleted,
  });

  const [deleteTag, { loading: deleteLoading }] = useMutation(
    DELETE_IDEA_CHANNEL_TAG,
    { onCompleted: handleCompleted }
  );

  const handleMenuClick: MenuProps['onClick'] = (info) => {
    if (idea.id) {
      createIdeaChannelTag({
        variables: {
          ideaId: idea.id,
          channelTagId: info.key,
        },
      });
    }
  };

  const deleteTagHandler = (id: string) => {
    deleteTag({
      variables: {
        ideaId: idea.id,
        channelTagId: id,
      },
      refetchQueries: [GET_CHANNEL_TAGS],
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {idea.channelTag?.map((item) => (
        <div
          key={item.id}
          className="px-3 py-1 bg-gray-200 text-xs rounded-full font-medium space-x-2"
        >
          <span>{item.name}</span>
          <Popconfirm
            title={t('confirm_delete_tag')}
            onConfirm={() => deleteTagHandler(item.id)}
            okButtonProps={{ loading: deleteLoading }}
            okText={t('yes')}
            cancelText={t('no')}
          >
            <span className="text-gray-700 cursor-pointer">
              <CloseOutlined />
            </span>
          </Popconfirm>
        </div>
      ))}
      <Dropdown
        menu={{
          items: menuItems,
          onClick: handleMenuClick,
        }}
        placement="bottomLeft"
        trigger={['click']}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          shape="round"
          size="small"
          ghost
          // className="!text-primary-600 !border-primary-600"
          data-test="idea-tag"
        >
          {t('add_tag')}
        </Button>
      </Dropdown>
    </div>
  );
}

export default IdeaChannelTagList;
